import { useEffect, useId, useRef, useState } from 'react'
import { fmt, type UiStrings } from '@/constants/ui-locale'
import type { SharedRoomMember } from '@/pages/Meetings/utils/sharedRoomPresentation'
import {
  sharedRoomAvatarTone,
  sharedRoomInitials,
  sharedRoomParticipantKey,
} from '@/pages/Meetings/utils/sharedRoomInitials'

export type WaitingInviteMember = {
  id: string
  name: string
}

export default function SharedRoomMembersPopover({
  members,
  waitingMembers = [],
  t,
  locale,
  onOpen,
}: {
  members: SharedRoomMember[]
  waitingMembers?: WaitingInviteMember[]
  t: UiStrings
  locale: string
  /** Fired when the panel opens so the parent can refetch pending shares. */
  onOpen?: () => void
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const panelId = useId()
  // Keep the latest callback without re-running the open effect — MeetingChat
  // passes an inline `onMembersOpen` that changes every WS-driven re-render.
  const onOpenRef = useRef(onOpen)
  onOpenRef.current = onOpen
  const wasOpenRef = useRef(false)

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      onOpenRef.current?.()
    }
    wasOpenRef.current = open
    if (!open) return undefined
    const onDoc = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const formatJoined = (joinedAt?: number) => {
    if (joinedAt === undefined) return null
    const time = new Date(joinedAt).toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
    })
    return fmt(t.sharedRoomMemberJoinedAt, { time })
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        data-testid="shared-room-members-trigger"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-2 py-1 text-xs font-semibold text-[var(--text-primary)] transition-colors hover:bg-[var(--bg-tertiary)]"
      >
        <span className="flex -space-x-1">
          {members.slice(0, 3).map((member, index) => (
            <span
              key={sharedRoomParticipantKey(member.name, index)}
              className={`flex h-6 w-6 items-center justify-center rounded-full border-2 border-[var(--bg-primary)] text-[9px] font-bold ${sharedRoomAvatarTone(member.name)}`}
            >
              {sharedRoomInitials(member.name)}
            </span>
          ))}
        </span>
        {/* Badge shows active room members only — Waiting lives in the panel. */}
        <span>{members.length}</span>
      </button>

      {open ? (
        <div
          id={panelId}
          role="dialog"
          aria-label={fmt(t.sharedRoomMembersTitle, { count: members.length })}
          data-testid="shared-room-members-panel"
          className="absolute right-0 top-full z-50 mt-2 w-64 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3 shadow-lg"
        >
          <p className="text-sm font-bold text-[var(--text-primary)]">
            {fmt(t.sharedRoomMembersTitle, { count: members.length })}
          </p>
          <ul className="mt-3 max-h-48 space-y-3 overflow-y-auto">
            {members.map((member, index) => (
              <li key={sharedRoomParticipantKey(member.name, index)} className="flex items-start gap-2">
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${sharedRoomAvatarTone(member.name)}`}
                >
                  {sharedRoomInitials(member.name)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1">
                    <span className="truncate text-sm font-medium text-[var(--text-primary)]">
                      {member.name}
                    </span>
                    {member.role === 'host' ? (
                      <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-800 dark:bg-violet-900/40 dark:text-violet-200">
                        {t.sharedRoomMemberHost}
                      </span>
                    ) : null}
                    {member.role === 'you' ? (
                      <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
                        {t.sharedRoomMemberYou}
                      </span>
                    ) : null}
                  </div>
                  {formatJoined(member.joinedAt) ? (
                    <p className="text-xs text-[var(--text-secondary)]">{formatJoined(member.joinedAt)}</p>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>

          {waitingMembers.length > 0 ? (
            <div className="mt-3 border-t border-[var(--border-color)] pt-3" data-testid="shared-room-invited-section">
              <p className="text-xs font-semibold text-[var(--text-secondary)]">
                {fmt(t.liveInviteInvitedHeading, { count: waitingMembers.length })}
              </p>
              <ul className="mt-2 max-h-36 space-y-3 overflow-y-auto">
                {waitingMembers.map((member) => (
                  <li key={member.id} className="flex items-start gap-2">
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${sharedRoomAvatarTone(member.name)}`}
                    >
                      {sharedRoomInitials(member.name)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-[var(--text-primary)]">
                        {member.name}
                      </span>
                      <span className="mt-0.5 inline-block text-xs text-[var(--text-tertiary)]">
                        {t.liveInviteWaiting}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
